import { useMutation, useQueryClient, UseMutationResult } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { getEmployeeUseCases } from '@personnel/composition';
import type { EmployeeBulkDeleteResponseDto } from '@personnel/application/dtos';
import { useApiErrorHandler } from './useApiErrorHandler';
import { EMPLOYEES_QUERY_KEYS } from './useDeleteEmployee';
import { ROLES_QUERY_KEYS } from './useRoleList';

/**
 * Hook options for configuring bulk delete behavior
 */
export interface UsePersonnelBulkDeleteEmployeesOptions {
  onSuccess?: (uuids: ReadonlyArray<string>) => void;
  onError?: (error: Error, uuids: ReadonlyArray<string>) => void;
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

/**
 * Hook result with mutation function and states
 */
export interface UsePersonnelBulkDeleteEmployeesResult {
  bulkDeleteEmployees: (uuids: ReadonlyArray<string>) => Promise<void>;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  reset: () => void;
}

/**
 * Hook for bulk deleting employees within Personnel domain
 *
 * Uses composition layer for dependency injection.
 * Automatically invalidates the employees list cache on success.
 *
 * @param {UsePersonnelBulkDeleteEmployeesOptions} options - Hook configuration options
 * @returns {UsePersonnelBulkDeleteEmployeesResult} Mutation result with typed interface
 *
 * @example
 * const { bulkDeleteEmployees, isLoading } = usePersonnelBulkDeleteEmployees({
 *   onSuccess: (uuids) => console.log('Deleted:', uuids),
 *   showSuccessToast: true
 * });
 *
 * const handleBulkDelete = async (uuids: string[]) => {
 *   await bulkDeleteEmployees(uuids);
 * };
 */
export const usePersonnelBulkDeleteEmployees = (
  options: UsePersonnelBulkDeleteEmployeesOptions = {},
): UsePersonnelBulkDeleteEmployeesResult => {
  const { onSuccess, onError, showSuccessToast = true, showErrorToast = true } = options;
  const queryClient = useQueryClient();
  const { t } = useTranslation('personnel-employees');
  const { handleError } = useApiErrorHandler();
  const { bulkDelete } = getEmployeeUseCases();

  const mutation: UseMutationResult<EmployeeBulkDeleteResponseDto, Error, ReadonlyArray<string>> = useMutation({
    mutationFn: async (uuids: ReadonlyArray<string>) => {
      return bulkDelete.execute({ uuids });
    },
    onSuccess: (response) => {
      // Invalidate employees list to refresh the table
      queryClient.invalidateQueries(EMPLOYEES_QUERY_KEYS.list());
      // Remove deleted employees from detail cache
      response.deletedUuids.forEach((uuid) => queryClient.removeQueries(EMPLOYEES_QUERY_KEYS.detail(uuid)));
      // Invalidate roles list to refresh usersCount
      queryClient.invalidateQueries(ROLES_QUERY_KEYS.list());

      if (showSuccessToast) {
        toast.success(t('notifications.deleteSuccess'), { duration: 3000 });
      }
      if (onSuccess) {
        onSuccess(response.deletedUuids);
      }
    },
    onError: (error, uuids) => {
      if (showErrorToast) {
        handleError(error);
      }
      if (onError) {
        onError(error, uuids);
      }
    },
  });

  /**
   * Bulk deletes employees by UUIDs
   * Wraps mutation to maintain void return type
   */
  const bulkDeleteEmployees = useCallback(
    async (uuids: ReadonlyArray<string>): Promise<void> => {
      if (!uuids || uuids.length === 0) {
        if (showErrorToast) {
          toast.error(t('notifications.deleteError'), { duration: 3000 });
        }
        return;
      }
      try {
        await mutation.mutateAsync(uuids);
      } catch {
        // Error is already handled by mutation's onError callback
      }
    },
    [mutation, showErrorToast, t],
  );

  return {
    bulkDeleteEmployees,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error || null,
    reset: mutation.reset,
  };
};
